import * as Cookies from "es-cookie";

// Branch scope helpers (branch_id concept, no employee_type_id).
// The login stores the logged-in user's authorized branch ids in the
// "branch_id" cookie as a comma-separated string (a single branch, several
// branches, or every institution branch when the user is unrestricted).

// The list of branch ids (as strings) the user is authorized to see.
export function getAuthorizedBranchIds() {
  const raw = Cookies.get("branch_id") || "";
  return String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
}

// True when the user is scoped to exactly one branch.
export function hasSingleBranch() {
  return getAuthorizedBranchIds().length === 1;
}

// Default value for a branch <select>/filter: the branch id when the user has
// exactly one authorized branch (auto-select it), otherwise "" (All Branches).
// Pass a fallback ("" or "0") to match the control's "all" sentinel.
export function defaultBranchValue(allValue = "") {
  const ids = getAuthorizedBranchIds();
  return ids.length === 1 ? ids[0] : allValue;
}

// Filter a branch list (from getBranches / globalBranches) down to the branches
// the logged-in user is authorized for. Client-side guard so branch dropdowns
// show only the user's branches even if the backend returns the full list.
// If the cookie carries no branch ids, the list is returned unchanged (safe).
export function filterAuthorizedBranches(branches, idKey = "id") {
  const allowed = getAuthorizedBranchIds();
  if (!allowed.length) return branches || [];
  const allow = new Set(allowed.map(String));
  return (branches || []).filter((b) => b && allow.has(String(b[idKey])));
}

// ── GLOBAL branch selection (header multi-select) ────────────────────────────
// The header (NavbarOne) carries a multi-select of the user's authorized
// branches. The choice is REMEMBERED across pages via the
// "selected_branch_ids" cookie and broadcast so open pages can refetch —
// mirroring the examScope.js pattern.
//
// This is a NARROWING of the authorized set, never a widening: an empty
// selection means "all branches I'm authorized for", and pages should send
// getEffectiveBranchIds() rather than the raw cookie so a user can never
// query a branch they aren't entitled to.

export const SELECTED_BRANCH_COOKIE = "selected_branch_ids";

// The globally-selected branch ids (as strings). [] = All authorized.
//
// The stored value is re-checked against the CURRENT user's authorized branches
// on every read, not just on write. The cookie outlives a session — it survives
// logout, an expired token, a restored browser tab, and an admin revoking a
// branch mid-session — so it can easily name branches the person now signed in
// has no access to. A selection with no overlap at all is treated as "nothing
// selected" (= all authorized) rather than as an empty scope, because otherwise
// the next user to sign in on this browser would find every page empty and the
// branch picker offering no options.
export function getSelectedBranchIds() {
  const raw = Cookies.get(SELECTED_BRANCH_COOKIE) || "";
  const stored = String(raw)
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  if (!stored.length) return [];

  const allowed = getAuthorizedBranchIds();
  if (!allowed.length) return stored; // unrestricted / admin
  const allow = new Set(allowed.map(String));
  return stored.filter((id) => allow.has(String(id)));
}

// Persist a new selection (pass [] to clear → All) and broadcast a change so
// open pages can refetch. Components listen via onBranchScopeChange().
export function setSelectedBranchIds(ids) {
  // Never persist a branch the user isn't authorized for.
  const allowed = new Set(getAuthorizedBranchIds().map(String));
  const clean = (ids || [])
    .map(String)
    .filter(Boolean)
    .filter((id) => !allowed.size || allowed.has(id));

  if (clean.length) Cookies.set(SELECTED_BRANCH_COOKIE, clean.join(","));
  else Cookies.remove(SELECTED_BRANCH_COOKIE);

  try {
    window.dispatchEvent(
      new CustomEvent("branchscopechange", { detail: { ids: clean } }),
    );
  } catch (e) {
    /* CustomEvent unsupported — cookie is still set; next navigation picks it up */
  }
}

// The branch ids a page should actually query with: the header selection when
// one exists, otherwise every branch the user is authorized for.
export function getEffectiveBranchIds() {
  const selected = getSelectedBranchIds();
  return selected.length ? selected : getAuthorizedBranchIds();
}

// Comma-separated form for GraphQL branch_id arguments ("" when unrestricted).
export function getEffectiveBranchCsv() {
  return getEffectiveBranchIds().join(",");
}

// Narrow a fetched branch list down to what the header currently selects.
// This is the FILTER-side counterpart of filterAuthorizedBranches: pages that
// used to own a branch dropdown now derive their branch set from this instead.
// An empty header selection means "all branches I'm authorized for".
export function scopeBranches(branches, idKey = "id") {
  const authorized = filterAuthorizedBranches(branches, idKey);
  const selected = getSelectedBranchIds();
  if (!selected.length) return authorized;
  const sel = new Set(selected.map(String));
  return authorized.filter((b) => b && sel.has(String(b[idKey])));
}

// ── DATA-ENTRY branch selects ────────────────────────────────────────────────
// Add/edit forms KEEP their own Branch dropdown — a saved record needs exactly
// one branch_id, and the header is a multi-select that names no single branch.
// What the header does control is the OPTION LIST: pass the fetched branches
// through scopeBranches() so a form can only file a record against a branch the
// user is currently working in.
//
// entryBranchDefault() then pre-selects that branch when the scope resolves to
// exactly one, so a single-branch user never has to pick it by hand.
export function entryBranchDefault(allValue = "") {
  const ids = getEffectiveBranchIds();
  return ids.length === 1 ? ids[0] : allValue;
}

// Subscribe to global branch-selection changes. Returns an unsubscribe fn.
//   const off = onBranchScopeChange(() => this.reload());
//   // later: off();
export function onBranchScopeChange(handler) {
  const wrapped = (e) => handler((e && e.detail && e.detail.ids) || []);
  window.addEventListener("branchscopechange", wrapped);
  return () => window.removeEventListener("branchscopechange", wrapped);
}
